import { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BOUNTY_MARK } from '@arthelper/shared/bounties/mark';
import { bountyPeriodNote } from '@arthelper/shared/bounties/period-note';
import { questPaletteFor } from '@arthelper/shared/quests/quest-palette';
import { useAnalytics } from '~/context/analytics';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useBandRotation, useReel } from './useBandRotation';
import '../questBand/quest-band.css';
import './bounty-band.css';

const TOWN_SQUARE = (typeof __TOWN_SQUARE_URL__ !== 'undefined' && __TOWN_SQUARE_URL__) || 'https://www.arthelper.com';

const MY_PINNED_BOUNTIES = gql`
  query ClientPinnedBounties {
    myPinnedBounties {
      id
      slug
      name
      emoji
      color
      pointsReward
      cadence
      joined
      paid
      periodKey
      periodStartsAt
      periodEndsAt
      periodTimeZone
      step {
        label
        progress
        complete
        count
        of
      }
    }
  }
`;

const BOUNTY_CHECK_IN = gql`
  mutation ClientBountyCheckIn {
    bountyCheckIn {
      paid
      notified
    }
  }
`;

type PinnedBounty = {
  id: string;
  slug: string;
  name: string;
  emoji: string | null;
  color: string | null;
  pointsReward: number;
  cadence: string;
  joined: boolean;
  paid: boolean;
  periodKey: string;
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  periodTimeZone: string | null;
  step: { label: string; progress: number; complete: boolean; count: number | null; of: number | null };
};

/**
 * True once the page has settled after the strip mounts. The icon's hello waits for it:
 * started on mount it plays while the page is still loading and is never seen (#4118).
 */
function useSettled(): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const begin = () => requestAnimationFrame(() => setSettled(true));
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(begin, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(begin, 800);
    return () => window.clearTimeout(id);
  }, []);
  return settled;
}

/** "Rate 3 pieces — 0 of 3", with the tally kept on one line when the title wraps. */
function StripLabel({ label }: { label: string }) {
  const cut = label.lastIndexOf(' — ');
  if (cut < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, cut)} <span className="bounty-strip-tally">{label.slice(cut + 1)}</span>
    </>
  );
}

/** The band's "next bounty" icon: two turning arrows (Tabler's refresh), so it never reads as the minimize chevron. */
function Cycle() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 11a8.1 8.1 0 0 0-15.5-2m-.5-4v4h4" />
      <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
    </svg>
  );
}

/** The band's up/down chevron: up collapses it, down pulls it back. */
function Chevron({ down = false }: { down?: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path
        d={down ? 'M3 5l4 4 4-4' : 'M3 9l4-4 4 4'}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * THE BOUNTY BAND, client-app edition (#4118). Counterpart of town-square's
 * `components/bounty/BountyBand.tsx` + `BountyStrip.tsx` — edit both or neither.
 *
 * A deliberate LITE port, as the client's quest band is: the step panels live only in
 * town-square, so this strip hands the bounty to its canonical page there with
 * `?open=1`, where the panel opens on arrival. Mounting it is the check-in.
 *
 * It turns to the next bounty every 10s and an arrow steps through them by hand. There is
 * no dismiss: she minimizes it to a tab, which a bounty she has not seen reopens (#4600).
 */
export function BountyBand() {
  const { isSignedIn } = useAuth();
  const { t, i18n } = useTranslation('reputation');
  const { enabled, loading: flagLoading } = useFeatureFlag('bounties');
  const active = Boolean(isSignedIn) && !flagLoading && enabled;
  const { data, refetch } = useQuery<{ myPinnedBounties: PinnedBounty[] }>(MY_PINNED_BOUNTIES, {
    skip: !active,
    fetchPolicy: 'cache-and-network',
  });
  const [checkIn] = useMutation(BOUNTY_CHECK_IN);
  const settled = useSettled();
  const { amplitude } = useAnalytics();
  const bounties = active ? (data?.myPinnedBounties ?? []) : [];
  // Held still while she is reading or reaching for it.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const onReopen = useCallback(
    () => amplitude.track('bounty_band_reopened', { count: bounties.length }),
    [amplitude, bounties.length]
  );
  const { index, count, next, minimized, minimize, expand } = useBandRotation(bounties, {
    paused: hovered || focused,
    onReopen,
  });
  const bandRef = useRef<HTMLDivElement>(null);
  useReel(bandRef, bounties[index]?.id ?? null);

  const checkedIn = useRef(false);
  useEffect(() => {
    if (!active || checkedIn.current) return;
    checkedIn.current = true;
    void checkIn()
      .catch(() => undefined)
      .then(() => refetch());
  }, [active, checkIn, refetch]);

  // Her button unmounts as the band swaps; keep keyboard focus on its opposite number.
  const refocus = useRef(false);
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    bandRef.current
      ?.querySelector<HTMLElement>(minimized ? '[data-testid="bounty-tab"]' : '[data-testid="bounty-strip-minimize"]')
      ?.focus();
  }, [minimized]);

  const bounty = bounties[index];
  if (!bounty) return null;

  if (minimized) {
    return (
      <div ref={bandRef} className="as-skin as-bar-wrap" data-skin="brand" data-testid="bounty-band">
        <div className="bounty-tab-row">
          <button
            type="button"
            className="bounty-tab"
            data-testid="bounty-tab"
            aria-label={`Show ${count} ${count === 1 ? 'bounty' : 'bounties'}`}
            aria-expanded="false"
            onClick={() => {
              amplitude.track('bounty_band_expanded', { count });
              refocus.current = true;
              expand();
            }}
          >
            <span className="bounty-tab-mark" aria-hidden="true">
              {BOUNTY_MARK}
            </span>
            {count} {count === 1 ? 'bounty' : 'bounties'}
            <Chevron down />
          </button>
        </div>
      </div>
    );
  }

  const palette = questPaletteFor(bounty.slug, bounty.color);
  // The town-square strip's note, word for word: "Today, Oct 21" in her own zone.
  const note = bountyPeriodNote(bounty, bounty.paid || bounty.step.complete, i18n.language);
  const when = note ? t(`bounty-period.${note.key}`, note.values) : null;

  return (
    <div
      ref={bandRef}
      className="as-skin as-bar-wrap"
      data-skin="brand"
      data-testid="bounty-band"
      // A tap is neither a hover nor keyboard focus: it fires a mouseenter with no leave and
      // leaves the arrow focused, which would hold the band still for good.
      onPointerEnter={(event) => setHovered(event.pointerType !== 'touch')}
      onPointerLeave={() => setHovered(false)}
      onFocus={(event) => setFocused(event.target.matches(':focus-visible'))}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <a
        className="as-bar-mini bounty-strip"
        style={{ background: palette.background, borderColor: palette.borderColor }}
        href={`${TOWN_SQUARE}/bounties/${bounty.slug}?open=1`}
        data-testid="bounty-strip"
      >
        <span className={`bounty-strip-kind${settled ? ' is-hello' : ''}`} aria-hidden="true">
          {BOUNTY_MARK}
        </span>
        <p className="as-bar-mini-heading">
          <span className="bounty-strip-label">Bounty{when ? ` · ${when}` : ''}</span>
          <span className="bounty-strip-sep" aria-hidden="true">
            ·
          </span>
          <StripLabel label={bounty.step.label} />
        </p>
        {bounty.step.of !== null && bounty.step.count !== null ? (
          <span className="as-bar-mini-track" aria-hidden="true">
            <span className="as-bar-mini-fill" style={{ width: `${Math.round(bounty.step.progress * 100)}%` }} />
          </span>
        ) : null}
        {bounty.pointsReward > 0 ? (
          <span className="as-bar-mini-points bounty-strip-points">
            <span className="bounty-strip-star" aria-hidden="true">
              ⭐
            </span>
            +{bounty.pointsReward.toLocaleString()} pts
          </span>
        ) : null}
        <span className="as-bar-mini-expand bounty-strip-cta">{bounty.joined ? 'Go →' : 'Join'}</span>
        {count > 1 ? (
          <span className="bounty-strip-step">
            <button
              type="button"
              className="bounty-strip-next"
              data-testid="bounty-strip-next"
              aria-label={`Next bounty (${index + 1} of ${count})`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                amplitude.track('bounty_band_next', { count, position: index + 1 });
                next();
              }}
            >
              <Cycle />
            </button>
            <span className="bounty-strip-count" aria-hidden="true">
              {index + 1} of {count}
            </span>
          </span>
        ) : null}
        <button
          type="button"
          className="bounty-strip-minimize"
          data-testid="bounty-strip-minimize"
          aria-label="Minimize bounties"
          aria-expanded="true"
          title="Minimize"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            amplitude.track('bounty_band_minimized', { count });
            // The tab replaces the strip under her pointer, so no leave or blur will come.
            setHovered(false);
            setFocused(false);
            refocus.current = true;
            minimize();
          }}
        >
          <Chevron />
        </button>
      </a>
    </div>
  );
}

/** Same seat and the same exclusions as the client's quest band. */
export function BountyBandGate() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/welcome') || pathname.startsWith('/admin')) return null;
  return <BountyBand />;
}
